import { useCallback, useEffect, useState } from "react";
import { newFailures, onlyKnown } from "../components/knownProblems";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Modal from "../components/Modal";
import StartTesting from "../components/StartTesting";
import { useTitle } from "../useTitle";
import VerdictBar from "../components/VerdictBar";
import { Board, Loading, Notice, PageHead, Row, Section, Status, type Tone } from "../components/Look";
import Build from "../components/Build";
import { campaignBuildKey, campaignBuildName } from "../components/campaignBuild";
import { ApiError, ago, api, atLeast, duration, type Bench, type Run, type Campaign } from "../api";
import i18n from "../i18n";

/**
 * What the rigs are doing, and what wants a look.
 *
 * THE PAGE THAT DID NOT EXIST. `/` redirected to Projects - a directory of
 * four cards on a 60%-empty screen - and the only live signal was the bar in
 * the shell, deliberately silent when nothing runs, which is most of the time.
 * So the question this service exists to answer, "did last night break
 * anything", cost four navigations: Runs, find the newest row for the right
 * bench, open it, read the "since run N" block.
 *
 * NOTHING HERE IS NEW DATA. `/live`, `/benches`, the run list's verdict
 * breakdown and the run detail's `transitions` were all being served already;
 * this is the first page to ask them the question together.
 *
 * The expensive half is bounded by the number of rigs, not by history: one
 * newest run per bench, and one detail fetch for each of those. It refreshes
 * on a slower clock than the live bar, because a finished run does not change.
 */

type Live = Awaited<ReturnType<typeof api.live>>["live"][number];
type Detail = Awaited<ReturnType<typeof api.run>>;

type Card = {
  bench: Bench;
  last?: Run;
  detail?: Detail;
};

export default function Now() {
  const { t } = useTranslation();
  useTitle(t("now.title"));
  const [live, setLive] = useState<Live[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  // The bench somebody asked to start testing on, from its card.
  const [starting, setStarting] = useState<Card["bench"] | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  const pullLive = useCallback(async () => {
    try {
      setLive((await api.live()).live);
    } catch {
      /* ambient: an error banner for a background poll covers the page */
    }
  }, []);

  const pullRigs = useCallback(async () => {
    try {
      const benches = (await api.benches()).benches;
      const out = await Promise.all(
        benches.map(async (bench): Promise<Card> => {
          try {
            const last = (await api.runs(bench.id, 1)).runs[0];
            if (!last) return { bench };
            return { bench, last, detail: await api.run(last.id) };
          } catch {
            // One rig that cannot be read must not blank the other two.
            return { bench };
          }
        }),
      );
      setCards(out);
      try {
        setCampaigns((await api.campaigns({ limit: 50 })).campaigns);
      } catch {
        /* a service from before campaigns simply has none */
      }
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void pullLive();
    const t = setInterval(() => void pullLive(), 10_000);
    return () => clearInterval(t);
  }, [pullLive]);

  useEffect(() => {
    void pullRigs();
    const t = setInterval(() => void pullRigs(), 60_000);
    return () => clearInterval(t);
  }, [pullRigs]);

  const attention = attentionOf(cards);
  const builds = latestPerBuild(campaigns);

  const online = cards.filter((c) => rigState(c.bench).tone === "up").length;
  const lastStarted = cards.reduce((m, c) => Math.max(m, c.last?.started_at ?? 0), 0);
  // The answer: what needs a look, else what is running, else that nothing
  // does - the question somebody opens Now with.
  const answer = attention.length
    ? t("now.answerAttention", { count: attention.length })
    : live.length
      ? t("now.answerRunning", { count: live.length })
      : t("now.answerQuiet");

  return (
    <section>
      <PageHead
        title={t("now.heading")}
        answer={loaded ? answer : undefined}
        bad={attention.some((f) => f.tone === "bad")}
        after={loaded && cards.length > 0 ? (
          <p>
            {t("now.rigs", { count: cards.length })}, {t("now.onlineCount", { count: online })}.
            {lastStarted ? ` ${t("now.lastRun", { when: ago(lastStarted) })}` : ""}
          </p>
        ) : undefined}
      />
      {!loaded && <Loading label={t("common.loading")} />}
      {error && <Notice tone="error">{error}</Notice>}

      {/* IS THIS BUILD GOOD (clear-screens stop E). The newest campaign of each
          build leads the page: a campaign plans every profile, so it is the
          answer to that question - the last run on one bench is not. */}
      {builds.length > 0 && (
        <Section title={t("now.headingBuilds")}>
          <Board>
            {builds.map((c) => (
              <BuildAnswer key={c.id} c={c}
                           bench={cards.find((x) => x.bench.id === c.bench_id)?.bench.name} />
            ))}
          </Board>
        </Section>
      )}

      {live.length > 0 && (
        <Section title={t("now.headingRunning")}>
          <Board>{live.map((r) => <Running key={`${r.run_id}-${r.name}`} r={r} />)}</Board>
        </Section>
      )}

      {attention.length > 0 && (
        <Section title={t("now.headingAttention")}>
          <Board>{attention.map((f) => <Finding key={f.key} f={f} />)}</Board>
        </Section>
      )}

      <Section title={t("now.headingRigs")}>
        {loaded && cards.length === 0 && (
          <p className="text-base text-slate-500">{t("now.noBenches")}</p>
        )}
        {cards.length > 0 && (
          <Board>{cards.map((c) => <Rig key={c.bench.id} card={c} onStart={setStarting} />)}</Board>
        )}
        {starting && (
          <Modal wide title={t("start.title")} onClose={() => setStarting(null)}>
            <StartTesting benches={cards.map((c) => c.bench)} benchId={starting.id} />
          </Modal>
        )}
      </Section>
    </section>
  );
}

/* ---------------------------------------------------------------- findings */

type Found = {
  key: string;
  tone: "bad" | "warn";
  label: string;
  title: React.ReactNode;
  body?: React.ReactNode;
};

/**
 * What is worth interrupting somebody for, per rig.
 *
 * ASSEMBLED, NOT STORED. Each of these is a fact one endpoint already knows
 * and no page put together: a verdict that moved the wrong way, a harness that
 * failed its own gate, a run that finished without the verdicts that make its
 * numbers mean anything, a lease nobody released, a rig that stopped talking.
 *
 * Exported for the test, and because the rule is worth reading in one place
 * rather than inferred from four blocks of JSX.
 */
/** Every rig's findings, at one moment.
 *
 *  NOT `cards.flatMap(findings)`: flatMap hands the callback the element's
 *  INDEX as its second argument, which `findings` takes as `now` - so the
 *  "quiet" rule measured silence from 1970 plus 0..3 seconds, came out
 *  negative and never fired. displayos-headless went offline on 17 September
 *  2026 with its nightly campaigns switched on, and for eleven days this page
 *  said "nothing newly broken". */
export function attentionOf(cards: Card[], now = Date.now() / 1000): Found[] {
  return cards.flatMap((c) => findings(c, now));
}

/** Whether a bench is set to start campaigns on its own. */
export function autoOn(b: Bench): boolean {
  try {
    const a = JSON.parse(b.auto_json || "{}") as { on_new_build?: boolean; nightly_utc?: string | null };
    return !!(a.on_new_build || a.nightly_utc);
  } catch {
    return false;
  }
}

export function findings(c: Card, now = Date.now() / 1000): Found[] {
  const out: Found[] = [];
  const { bench, last, detail } = c;

  const broke = detail?.transitions?.broke ?? [];
  if (last && broke.length > 0) {
    out.push({
      key: `broke-${bench.id}`,
      tone: "bad",
      label: i18n.t("now.brokeLabel", { count: broke.length }),
      title: (
        <Trans
          i18nKey="now.brokeTitle"
          values={{ run: last.name, bench: bench.name, when: ago(last.started_at) }}
          components={{
            runLink: (
              <Link className="font-semibold underline" to={`/runs/${last.id}`} />
            ),
          }}
        />
      ),
      body: (
        <span className="font-mono text-xs">
          {broke.map((b) => b.scenario).join(", ")}
        </span>
      ),
    });
  }

  // A harness that fails its own tests cannot make a trustworthy claim about
  // the device, and that is not a fact about the product.
  if (last && detail?.selftest?.ok === false) {
    out.push({
      key: `gate-${bench.id}`,
      tone: "bad",
      label: i18n.t("now.gateLabel"),
      title: (
        <Trans
          i18nKey="now.gateTitle"
          values={{ run: last.name }}
          components={{
            runLink: (
              <Link className="font-semibold underline" to={`/runs/${last.id}`} />
            ),
          }}
        />
      ),
    });
  }

  // Numbers without verdicts are failed CHECKS, and a draft that fails a check
  // is an expected failure rather than a regression.
  if (last?.finished_at && (last.verdict_count ?? 0) === 0 && last.step_count > 0) {
    out.push({
      key: `nojunit-${bench.id}`,
      tone: "warn",
      label: i18n.t("now.noJunitLabel"),
      title: (
        <Trans
          i18nKey="now.noJunitTitle"
          values={{ run: last.name }}
          components={{
            runLink: (
              <Link className="font-semibold underline" to={`/runs/${last.id}`} />
            ),
          }}
        />
      ),
    });
  }

  if (bench.lease && bench.lease.seconds_left <= 0) {
    out.push({
      key: `lease-${bench.id}`,
      tone: "warn",
      label: i18n.t("now.leaseLabel"),
      title: (
        <Trans
          i18nKey={bench.lease.purpose ? "now.leaseTitleFor" : "now.leaseTitle"}
          values={{
            actor: bench.lease.actor,
            bench: bench.name,
            purpose: bench.lease.purpose,
          }}
          components={{
            b: <b />,
            benchLink: <Link className="underline" to={`/benches/${bench.id}`} />,
          }}
        />
      ),
    });
  }

  // A day, not two minutes: `online` already covers the heartbeat, and a rig
  // that is simply switched off between campaigns is not a finding.
  //
  // NO THRESHOLD ON `has_agent`, and that asymmetry with the label above is the
  // point. A bench that has never pulsed has no agent that could have died, so
  // there is nothing to act on however long the silence runs - and the label's
  // 24-hour window, applied here, would put the DisplayOS rig back in this list
  // after every weekend. A section that always holds one item nobody can act on
  // is a section people stop reading, which is the only thing this page has to
  // sell.
  //
  // The cost is taken knowingly: while the column is new, this also silences a
  // genuinely dead agent on a bench that has not pulsed since the field
  // shipped. It stays visible as "offline" on its own row, and a supervising
  // agent pulses even when idle, so the case closes itself within one
  // interval.
  if (
    bench.has_agent !== false &&
    bench.last_seen_at &&
    !bench.online &&
    now - bench.last_seen_at > 86_400
  ) {
    out.push({
      key: `quiet-${bench.id}`,
      tone: "warn",
      label: i18n.t("now.quietLabel"),
      title: (
        <Trans
          i18nKey="now.quietTitle"
          values={{ bench: bench.name, when: ago(bench.last_seen_at) }}
          components={{
            benchLink: <Link className="underline" to={`/benches/${bench.id}`} />,
          }}
        />
      ),
      // The silence that costs most: a bench trusted to test every night,
      // which stopped without anybody being told.
      body: autoOn(bench) ? i18n.t("now.quietAuto") : undefined,
    });
  }

  return out;
}

function Finding({ f }: { f: Found }) {
  return (
    <Row status={<Status tone={f.tone === "bad" ? "bad" : "warn"}>{f.label}</Status>}
         title={f.title} sub={f.body} />
  );
}

/* ------------------------------------------------------------------ live */

function Running({ r }: { r: Live }) {
  const { t } = useTranslation();
  let total: number | null = null;
  if (r.step) {
    try {
      total = (JSON.parse(r.step.raw_json) as { total?: number }).total ?? null;
    } catch {
      total = null;
    }
  }
  const pct = total && r.step?.step_no ? Math.round((100 * r.step.step_no) / total) : null;
  return (
    <Row
      status={<Status tone="live">{t("verdict.running")}</Status>}
      title={
        <>
          {r.run_id ? (
            <Link to={`/runs/${r.run_id}`} className="font-semibold underline">{r.name}</Link>
          ) : (
            <span className="font-semibold">{r.name}</span>
          )}{" "}
          <span className="text-slate-600 dark:text-slate-400">
            <Trans
              i18nKey="live.onBench"
              values={{ bench: r.bench_name }}
              components={{
                benchLink: <Link to={`/benches/${r.bench_id}`} className="underline" />,
              }}
            />
          </span>
        </>
      }
      sub={
        r.step ? (
          <span className="break-words">
            {r.step.scenario},{" "}
            {total
              ? t("live.stepOf", { no: r.step.step_no ?? t("live.unknownStep"), total })
              : t("live.step", { no: r.step.step_no ?? t("live.unknownStep") })}
            , {r.step.action}
          </span>
        ) : r.starting ? t("now.selfTests") : undefined
      }
      aside={
        <span className="whitespace-nowrap">
          {r.step_count > 0 ? `${t("run.steps", { count: r.step_count })}, ` : ""}
          {duration(r.started_at, null)}
        </span>
      }
    >
      {pct !== null && (
        <div className="mt-2 h-1.5 overflow-hidden rounded bg-sky-100 dark:bg-sky-900 sm:ml-[11rem]">
          <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
        </div>
      )}
    </Row>
  );
}

/* ------------------------------------------------------------------ rigs */

/**
 * A rig's state in one line.
 *
 * "Offline" is not always a fault. A container rig with no agent - the
 * DisplayOS one is a container on a laptop publishing with `run.sh --once` -
 * is never online by design, and painting it the same grey as a rig that has
 * genuinely gone quiet sends somebody looking for a fault that is not there.
 * The two are told apart by whether an agent has EVER reported.
 */
/** A publish is fresh enough to prove the rig is still in use. Same day as the
    "quiet" finding, deliberately: they answer the same question about the same
    silence. */
const FRESH = 24 * 3600;

export function rigState(
  b: Bench,
  now = Date.now() / 1000,
): { text: string; tone: "up" | "down" | "none"; why: string } {
  if (b.agent_alive ?? b.online)
    return { text: i18n.t("now.stateOnline"), tone: "up", why: i18n.t("now.whyOnline") };

  // PUBLISHED RECENTLY, NEVER PULSED. `last_seen_at` is raised by publishing a
  // run; `last_heartbeat_at`, which is what `has_agent` reads, is not. So the
  // pair is not a heuristic - it is the definition of a rig that publishes
  // without an agent, and it needs nothing about the runs themselves.
  //
  // The first draft asked only whether an agent had ever pulsed. That is wrong
  // while the column is new: every bench starts false, so ipi6-garage - which
  // HAS an agent, on a unit that is switched off - would be labelled as one
  // that never could. Requiring a recent publish excludes it, and the era ends
  // by itself, because a supervising agent pulses even when idle.
  if (b.has_agent === false && b.last_seen_at && now - b.last_seen_at < FRESH) {
    return {
      text: i18n.t("now.statePublishes"),
      tone: "none",
      why: i18n.t("now.whyPublishes"),
    };
  }
  if (!b.last_seen_at) {
    return {
      text: i18n.t("now.statePublishes"),
      tone: "none",
      why: i18n.t("now.whyPublishes"),
    };
  }
  return {
    text: i18n.t("now.stateOffline"),
    tone: "down",
    why: i18n.t("now.whyOffline"),
  };
}

function Rig({ card, onStart }: { card: Card; onStart: (b: Card["bench"]) => void }) {
  const { t } = useTranslation();
  const { bench, last } = card;
  const st = rigState(bench);
  const tone: Tone = st.tone === "up" ? (bench.lease ? "warn" : "good") : "off";
  const word = st.tone === "up" ? (bench.lease ? t("now.onlineHeld") : t("now.onlineFree")) : st.text;
  // Queueing is where hardware MOVES, and the server decides. The button is
  // here because this is where somebody looks first; it opens the same Start
  // testing form as the bench and the project, and the form says what it costs.
  const canStart = atLeast(bench.role, "operator") && st.tone === "up" && !bench.lease;
  return (
    <Row
      status={<Status tone={tone} title={st.why}>{word}</Status>}
      title={
        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <Link to={`/benches/${bench.id}`} className="font-semibold hover:underline">{bench.name}</Link>
          {bench.project_name && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {bench.project_name}
            </span>
          )}
        </span>
      }
      sub={
        <div className="space-y-1">
          {bench.lease && (
            <p className="text-amber-800 dark:text-amber-300">
              <Trans
                i18nKey={bench.lease.purpose ? "now.heldByFor" : "now.heldBy"}
                values={{ actor: bench.lease.actor, purpose: bench.lease.purpose }}
                components={{ b: <b /> }}
              />
            </p>
          )}
          {last ? (
            <>
              <VerdictBar run={last} />
              <p className="flex flex-wrap items-baseline gap-x-2">
                <span>
                  <Link className="underline" to={`/runs/${last.id}`}>{last.name}</Link>
                  {", "}{ago(last.started_at)}
                  {last.finished_at ? `, ${t("runDetail.took", { duration: duration(last.started_at, last.finished_at) })}` : ""}
                </span>
                {last.build_recorded && (
                  <Build build={last.build} ref_={last.build_ref} recorded showRef={false}
                         variant={last.variant} variantLabel={last.variant_label} />
                )}
              </p>
            </>
          ) : (
            <p>{t("now.nothingReported")}</p>
          )}
        </div>
      }
      aside={
        <>
          <span className="whitespace-nowrap">
            {bench.last_seen_at ? t("now.seen", { when: ago(bench.last_seen_at) }) : t("now.neverSeen")}
          </span>
          <Link className="btn-quiet" to={`/runs?bench=${bench.id}`}>{t("now.runsLink")}</Link>
          {canStart && (
            <button type="button" className="btn-primary" onClick={() => onStart(bench)}>
              {t("now.takeAndRun")}
            </button>
          )}
        </>
      }
    />
  );
}

/** The newest campaign of each build, newest first, at most five. A campaign
    for "whatever is installed" names no build, so it counts per bench. */
export function latestPerBuild(list: Campaign[]): Campaign[] {
  const seen = new Set<string>();
  return [...list]
    .sort((a, b) => b.created_at - a.created_at || b.id - a.id)
    .filter((c) => {
      const key = campaignBuildKey(c);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5);
}

/** One build's answer in a sentence, from its newest campaign. */
export function BuildAnswer({ c, bench }: { c: Campaign; bench?: string }) {
  const { t } = useTranslation();
  const build = c.build_ref
    ? t("now.buildNamed", { build: campaignBuildName(c) })
    : t("now.installedOn", { bench: bench ?? `#${c.bench_id}` });
  const v = { build, planned: c.planned ?? "?", ran: c.ran };
  const text =
    c.status === "green"
      ? t("now.buildGreen", v)
      : c.status === "running"
        ? t("now.buildRunning", v)
        : c.status === "cancelled"
          ? t("now.buildCancelled", v)
          : onlyKnown(c)
            ? t("now.buildOnlyKnown", { ...v, n: c.known })
            : newFailures(c)
              ? t("now.buildFailed", { ...v, count: newFailures(c) })
              : c.lost
                ? t("now.buildLost", v)
                : t("now.buildNotAllRan", v);
  const tone: Tone =
    c.status === "green" ? "good" : c.status === "running" ? "live"
      : c.status === "cancelled" ? "off" : newFailures(c) || c.lost ? "bad" : "warn";
  const word =
    c.status === "green" ? t("verdict.legend.passed")
      : c.status === "running" ? t("verdict.running")
        : c.status === "cancelled" ? t("campaign.status.cancelled")
          : onlyKnown(c) ? t("known.word")
            : newFailures(c) ? t("verdict.failed", { count: newFailures(c) }) : t("campaign.status.not_green");
  return (
    <Row status={<Status tone={tone}>{word}</Status>} title={text}
         aside={<Link className="underline" to={`/campaigns/${c.id}`}>{t("now.openCampaign")}</Link>} />
  );
}

